import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router'
import { SetRequired } from 'type-fest'

import { getOnlyDate } from '~/astra'
import { Schemas } from '~/clients/api-client'
import { getErrorMessageByStatusCode } from '~/hooks/tanstack-query'
import {
    archiveCommentsThread,
    createCommentsThread,
    createCommentsThreadMessage,
    createCommentsThreadMessageReaction,
    createCommentsThreadReaction,
    deleteCommentsThreadMessageReaction,
    deleteCommentsThreadReaction,
    editCommentsThread,
    editCommentsThreadMessage,
    pinCommentsThread,
    resolveCommentsThread,
    unpinCommentsThread,
    unresolveCommentsThread,
} from '~/store/di-entity.api'
import { importCommentsThreads } from '~/store/di-entity.api.generated'
import { SidebarSelection } from '~/store/slices/selectionDrawerSlice'
import { dispatchErrorToast } from '~/store/slices/toasterSlice'
import { useStore } from '~/store/store'
import { CommentsThread, selectCommentThreadsBySelection } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'

import {
    isDateInThreadsFilterRange,
    isLikelyOwner,
    selectionToThreadsFilter,
    selectionTypeToThreadRefType,
    ThreadContentPayload,
    ThreadsQueryFilter,
} from '../Commenting/utils'
import { ReactionString } from './reactions'

export type CreateThreadRef = Schemas['CreateCommentsThreadRequest']['ref']

export type PlanningPeriodSelection = { type: 'planningPeriod'; planningPeriodId: number }
export type ThreadSelection = SidebarSelection | PlanningPeriodSelection

const getCreateThreadParamsForSelection = (selection: ThreadSelection): CreateThreadRef => {
    if (selection.type === 'scheduleDate') {
        return {
            schedule_date: getOnlyDate(selection.date),
            ref_type: selectionTypeToThreadRefType[selection.type],
        }
    }
    if (selection.type === 'planningPeriod') {
        return {
            ref_type: 'planning_period_thread_ref',
            planning_period_id: selection.planningPeriodId,
        }
    }
    return {
        location_id: selection.locationId,
        schedule_date: getOnlyDate(selection.date),
        ref_type: selectionTypeToThreadRefType[selection.type],
    }
}

export const useImportCommentThreadsQuery = ({
    threadsFilter,
    initialData,
}: {
    threadsFilter?: Parameters<typeof importCommentsThreads>[0]
    placeholderData?: CommentsThread[]
    initialData?: CommentsThread[]
} = {}) => {
    // dont load archived threads by default (override with threadsFilter if needed)
    const filter = { archived: false, ...threadsFilter }
    const queryResponse = useQuery({
        queryKey: ['commentsThreads', filter],
        queryFn: async () => {
            const response = await importCommentsThreads(filter)
            return response.data
        },
        staleTime: 60 * 5 * 1000,
        initialData: initialData,
    })

    return queryResponse
}

export const useImportCommentThreadsForSelection = ({ selection, filter }: { selection: ThreadSelection; filter?: ThreadsQueryFilter }) => {
    const queryClient = useQueryClient()

    const threadsFilter: ThreadsQueryFilter = { ...filter, ...selectionToThreadsFilter(selection) }
    const queryState = queryClient.getQueryCache().find({
        queryKey: ['commentsThreads'],
        exact: false,
        predicate: q => {
            if (!('date' in selection)) {
                return false
            }
            const threadsFilterQueryKeyPart = q.queryKey[1] as ThreadsQueryFilter
            return isDateInThreadsFilterRange(selection.date, threadsFilterQueryKeyPart)
        },
    })?.state
    const hasInitialData = queryState?.status === 'success'

    const currentData = selectCommentThreadsBySelection(useStore.getState(), selection)

    return useImportCommentThreadsQuery({
        threadsFilter,
        initialData: hasInitialData ? currentData : undefined,
    })
}

type CreateCommentThreadMutationProps = {
    selection: ThreadSelection
}

export const useCreateCommentThreadMutation = ({ selection }: CreateCommentThreadMutationProps) => {
    const { pathname } = useLocation()
    return useMutation({
        mutationFn: ({ raw_content, mentions }: ThreadContentPayload) =>
            createCommentsThread({
                ref: getCreateThreadParamsForSelection(selection),
                raw_content,
                mentions: mentions && mentions.length > 0 ? mentions : undefined,
                source_path: pathname,
            }),
        onSuccess: (data, variables) => {
            trackUserEvent('COMMENT_THREAD_CREATED', {
                type: selection.type,
                mentionsCount: variables.mentions?.length ?? 0,
                contentLength: variables.raw_content.length,
                sourcePath: pathname,
            })
        },
        onError: () => {
            dispatchErrorToast({ message: getErrorMessageByStatusCode() })
        },
    })
}

export const useCreateCommentMessageMutation = () =>
    useMutation({
        mutationFn: ({ threadId, raw_content, mentions }: { threadId: number } & ThreadContentPayload) =>
            createCommentsThreadMessage(threadId, {
                raw_content,
                mentions: mentions && mentions.length > 0 ? mentions : undefined,
            }),
        onSuccess: (data, variables) => {
            trackUserEvent('COMMENT_MESSAGE_CREATED', {
                threadId: variables.threadId,
                mentionsCount: variables.mentions?.length ?? 0,
                contentLength: variables.raw_content.length,
            })
        },
        onError: () => {
            dispatchErrorToast({ message: getErrorMessageByStatusCode() })
        },
    })

export type UseCommentsProps = {
    selection: ThreadSelection
    filter?: ThreadsQueryFilter
}

type ToggleReactionOptions = {
    thread: CommentsThread
    replyId?: number
    reaction: ReactionString
}

type EditContentOptions = {
    thread: CommentsThread
    messageId?: number
    raw_content: string
    mentions?: Schemas['ResourceMention'][] | null
}

export const useCommentThreads = ({ selection, filter }: UseCommentsProps) => {
    const user = useStore(state => state.user)
    const resolvedUsername = user.display_name ?? user.username ?? 'Unknown'

    const { isLoading } = useImportCommentThreadsForSelection({ selection, filter })

    const createThreadMutation = useCreateCommentThreadMutation({ selection })
    const createMessageMutation = useCreateCommentMessageMutation()

    const toggleThreadMessageReaction = async (thread: CommentsThread, options: SetRequired<ToggleReactionOptions, 'replyId'>) => {
        const message = thread.messages.find(message => message.id === options.replyId)

        const existingReaction = message?.reactions.find(reaction => reaction.reaction === options.reaction && isLikelyOwner(reaction, user))
        if (existingReaction) {
            await deleteCommentsThreadMessageReaction(options.replyId, existingReaction.id)
            trackUserEvent('COMMENT_REACTION_REMOVED', {
                threadId: thread.id,
                reaction: options.reaction,
                isReply: true,
                messageId: options.replyId,
            })
        } else {
            await createCommentsThreadMessageReaction(options.replyId, {
                reaction: options.reaction,
            })
            trackUserEvent('COMMENT_REACTION_ADDED', {
                threadId: thread.id,
                reaction: options.reaction,
                isReply: true,
                messageId: options.replyId,
            })
        }
    }

    const toggleThreadReaction = async (thread: CommentsThread, options: ToggleReactionOptions) => {
        const existingReaction = thread.reactions.find(reaction => reaction.reaction === options.reaction && isLikelyOwner(reaction, user))
        if (existingReaction) {
            await deleteCommentsThreadReaction(options.thread.id, existingReaction.id)
            trackUserEvent('COMMENT_REACTION_REMOVED', {
                threadId: thread.id,
                reaction: options.reaction,
                isReply: false,
            })
        } else {
            await createCommentsThreadReaction(options.thread.id, {
                reaction: options.reaction,
            })
            trackUserEvent('COMMENT_REACTION_ADDED', {
                threadId: thread.id,
                reaction: options.reaction,
                isReply: false,
            })
        }
    }
    const toggleReaction = async (options: ToggleReactionOptions) => {
        const thread = options.thread
        if (options.replyId !== undefined) {
            await toggleThreadMessageReaction(thread, { ...options, replyId: options.replyId })
        } else {
            await toggleThreadReaction(thread, options)
        }
    }

    const toggleResolveThread = async (thread: CommentsThread) => {
        if (thread.resolved) {
            await unresolveCommentsThread(thread.id)
            trackUserEvent('COMMENT_THREAD_UNRESOLVED', {
                threadId: thread.id,
            })
        } else {
            await resolveCommentsThread(thread.id, resolvedUsername)
            trackUserEvent('COMMENT_THREAD_RESOLVED', {
                threadId: thread.id,
            })
        }
    }

    const archiveThread = async (thread: CommentsThread) => {
        await archiveCommentsThread(thread.id, resolvedUsername)
        trackUserEvent('COMMENT_THREAD_ARCHIVED', {
            threadId: thread.id,
        })
    }

    const togglePinThread = async (thread: CommentsThread) => {
        if (thread.pinned) {
            await unpinCommentsThread(thread.id)
            trackUserEvent('COMMENT_THREAD_UNPINNED', {
                threadId: thread.id,
            })
        } else {
            await pinCommentsThread(thread.id, resolvedUsername)
            trackUserEvent('COMMENT_THREAD_PINNED', {
                threadId: thread.id,
            })
        }
    }

    const editContent = async (options: EditContentOptions) => {
        if (options.messageId !== undefined) {
            await editCommentsThreadMessage(options.messageId, { ...options, updated_by: resolvedUsername })
            trackUserEvent('COMMENT_CONTENT_EDITED', {
                threadId: options.thread.id,
                contentLength: options.raw_content.length,
                mentionsCount: options.mentions?.length ?? 0,
                isReply: true,
            })
        } else {
            await editCommentsThread(options.thread.id, { ...options, updated_by: resolvedUsername })
            trackUserEvent('COMMENT_CONTENT_EDITED', {
                threadId: options.thread.id,
                contentLength: options.raw_content.length,
                mentionsCount: options.mentions?.length ?? 0,
                isReply: false,
            })
        }
    }

    return {
        isLoading,
        createThreadMutation,
        createMessageMutation,
        toggleReaction,
        toggleResolveThread,
        archiveThread,
        togglePinThread,
        editContent,
    }
}
